import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import stickyFooterContainerStyles from './StickyFooterContainerStyles';
import { IFinanceQuote } from '../../../../common/model/IFinanceQuote';
import useIsMobile from '../../../../common/react/useIsMobile';
import ButtonViewDetails from '../../../../common/components/sticky/ButtonViewDetails';
import { Box } from '@material-ui/core';
import currentDealSelectors from '../../../store/selectors/currentDealSelectors';
import scrollInView, { ScrollInViewBehaviourType, ScrollInViewType } from '../../../../common/service/scrollInView';
import carConfigurationSelectors from '../../../store/selectors/carConfigurationSelectors';
import useToggleFinCalcSpc from '../../../../common/react/useToggleFinCalcSpc';
import FinanceCalculatorContainer from '../financeCalculator/FinanceCalculatorContainer';
import { CustomiseSections } from '../../../../common/enum/CustomiseSections';
import StickyErrorContainer from './stickyError/StickyErrorContainer';
import useIsTablet from '../../../../common/react/useIsTablet';
import useScrollToSection from '../../../react/navigation/useScrollToSection';
import ActionButtonsGridElement from './actionButtonsGridElement/ActionButtonsGridElement';
import FinanceProductGridElement from './financeProductGridElement/FinanceProductGridElement';
import DotsLoader from '../../../../common/components/dotsLoader/DotsLoader';
import usePriceLoadingState from '../../../react/usePriceLoadingState';
import customise from '../../../constants/customise';
import { debounce } from 'throttle-debounce';
import usePlatformConfig from '../../../../common/react/usePlatformConfig';
import StockCta from './stockCTA/StockCTA';
import { routeUtils } from '../../../../common/utils/routeUtils';
import { useHistory } from 'react-router';
import useStockUrl from '../../../react/navigation/useStockUrl';

const StickyFooterContainer: FunctionComponent = () => {
  const style = stickyFooterContainerStyles();
  const isMobile = useIsMobile();
  const history = useHistory();
  const isTablet = useIsTablet();
  const { scrollToSection } = useScrollToSection();
  const dealError = useSelector(currentDealSelectors.getErrorStatus);
  const { isFinCalcSpcModalOpen, toggleFinanceCalculatorSpc } = useToggleFinCalcSpc();
  const financeQuote: IFinanceQuote = useSelector(currentDealSelectors.getFinanceQuote);
  const carConfiguration = useSelector(carConfigurationSelectors.getConfiguration);
  const isDataLoading = usePriceLoadingState();
  const [initialLoad, setInitialLoad] = useState(true);
  const [isSummaryInView, setIsSummaryInView] = useState(false);
  const summarySection = document.getElementById(`${customise.DOM_ID_PREFIX}${CustomiseSections.SUMMARY}`);
  const isMultiCurrency: boolean = useSelector(currentDealSelectors.getIsMultiCurrency);
  const shouldDisableCta = !!dealError || isDataLoading;
  const shouldDisplaySummaryLabel: boolean = useMemo(
    () => !!summarySection && (isMobile || isTablet),
    [isMobile, isTablet, summarySection],
  );
  const {
    platformConfig: { stockPage },
  } = usePlatformConfig();
  const { listUrl } = useStockUrl();

  const onViewDetailsClick = useCallback(() => {
    scrollToSection(CustomiseSections.SUMMARY, ScrollInViewBehaviourType.SMOOTH);
  }, []);

  useEffect(() => {
    if (!isDataLoading && initialLoad) {
      setInitialLoad(false);
    }
  }, [isDataLoading, initialLoad]);

  const checkSummaryVisibility = debounce(50, () => {
    const summary: HTMLElement | null = document.getElementById(
      `${customise.DOM_ID_PREFIX}${CustomiseSections.SUMMARY}`,
    );

    const isInView: boolean = summary ? scrollInView.isInView(summary, ScrollInViewType.ANY) : false;

    return setIsSummaryInView(isInView);
  });

  useEffect(() => {
    document.addEventListener('scroll', checkSummaryVisibility);

    return () => {
      document.removeEventListener('scroll', checkSummaryVisibility);
    };
  }, []);

  if (isDataLoading && initialLoad && !dealError) {
    return (
      <Box className={style.footerWrapperLoader} data-test="container:sticky_footer_loader">
        <DotsLoader />
      </Box>
    );
  }

  return (
    <>
      <Grid
        container={true}
        alignItems="center"
        className={clsx(style.footerWrapper, { [style.hideOnMobile]: isSummaryInView })}
        data-test="container:sticky_footer"
      >
        <StickyErrorContainer />
        <FinanceProductGridElement
          setIsFinCalcSpcModalOpen={toggleFinanceCalculatorSpc}
          isFinCalcSpcModalOpen={isFinCalcSpcModalOpen}
          isCash={!!financeQuote?.isCashProduct}
          isMultiCurrency={isMultiCurrency}
          isCtaDisabled={shouldDisableCta}
        />
        <ActionButtonsGridElement disabled={shouldDisableCta} />
        {stockPage && (isMobile || isTablet) && (
          <StockCta onClick={() => routeUtils.pushWithQueryParams(history, listUrl)} />
        )}
        {shouldDisplaySummaryLabel && (
          <ButtonViewDetails
            onClick={onViewDetailsClick}
            position={'top'}
            captionId={'spc.customise.footer.viewSummary'}
          />
        )}
      </Grid>
      {carConfiguration && (
        <FinanceCalculatorContainer
          externalId={carConfiguration?.externalId}
          carConfiguration={carConfiguration}
          isModalOpen={isFinCalcSpcModalOpen}
          toggleModal={toggleFinanceCalculatorSpc}
        />
      )}
    </>
  );
};

export default StickyFooterContainer;
